import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { ArrowLeft, Plus, Trash2, GripVertical, Calendar, Clock, Send, X } from 'lucide-react';
import { fetchDestinations, fetchItineraries, resolveImageUrl } from '../lib/travelApi';
import { toast } from 'sonner';

const TripBuilderPage = () => {
  const { destinationId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [destination, setDestination] = useState(null);
  const [destinationItineraries, setDestinationItineraries] = useState([]);
  const [loading, setLoading] = useState(true);

  const [mode, setMode] = useState(null); // 'prebuilt' or 'scratch'
  const [selectedItinerary, setSelectedItinerary] = useState(null);
  const [customDays, setCustomDays] = useState([]);
  const [draggedIndex, setDraggedIndex] = useState(null);

  useEffect(() => {
    Promise.all([fetchDestinations(), fetchItineraries(destinationId)])
      .then(([dests, itins]) => {
        setDestination(dests.find((d) => String(d.id) === String(destinationId)) || null);
        setDestinationItineraries(
          itins.filter((i) => String(i.destination_id) === String(destinationId))
        );
      })
      .catch(() => setDestination(null))
      .finally(() => setLoading(false));
  }, [destinationId]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  if (!destination) {
    return <div className="min-h-screen flex items-center justify-center">Destination not found</div>;
  }

  // Map activity name -> price (INR) for showing prices in the builder
  const priceByName = {};
  (destination.activities || []).forEach((a) => { priceByName[a.name] = a.price || 0; });
  const fmtINR = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;
  const itineraryTotal = customDays.reduce(
    (sum, day) => sum + (day.activities || []).reduce((s, a) => s + (priceByName[a] || 0), 0),
    0
  );

  const handleSelectPrebuilt = (itinerary) => {
    setSelectedItinerary(itinerary);
    setCustomDays(itinerary.days.map(day => ({
      ...day,
      activities: [...day.activities]
    })));
    setMode('prebuilt');
  };

  const handleStartFromScratch = () => {
    setCustomDays([
      { day: 1, title: 'Day 1', activities: ['Airport pickup'] }
    ]);
    setMode('scratch');
  };

  const addDay = () => {
    setCustomDays([
      ...customDays,
      { day: customDays.length + 1, title: `Day ${customDays.length + 1}`, activities: [] }
    ]);
  };

  const removeDay = (index) => {
    if (customDays.length <= 1) {
      toast.error('Trip must have at least one day');
      return;
    }
    const newDays = customDays.filter((_, i) => i !== index).map((day, i) => ({
      ...day,
      day: i + 1,
      title: day.title.startsWith('Day ') ? `Day ${i + 1}` : day.title
    }));
    setCustomDays(newDays);
  };

  const updateDayTitle = (index, title) => {
    const newDays = [...customDays];
    newDays[index].title = title;
    setCustomDays(newDays);
  };

  const addActivityToDay = (dayIndex, activity) => {
    const newDays = [...customDays];
    if (!newDays[dayIndex].activities.includes(activity)) {
      newDays[dayIndex].activities.push(activity);
      setCustomDays(newDays);
    }
  };

  const removeActivityFromDay = (dayIndex, activityIndex) => {
    const newDays = [...customDays];
    newDays[dayIndex].activities.splice(activityIndex, 1);
    setCustomDays(newDays);
  };

  const handleSubmit = () => {
    if (customDays.length === 0) {
      toast.error('Please add at least one day to your trip');
      return;
    }
    navigate('/contact', {
      state: {
        destination: destination.name,
        tripType: 'custom-itinerary',
        itinerary: customDays,
        basedOn: selectedItinerary?.name || 'Custom Built'
      }
    });
  };

  // Selection Screen
  if (!mode) {
    return (
      <section className="min-h-screen py-20 bg-gray-50">
        <div className="container mx-auto px-4 max-w-5xl">
          <Button
            variant="outline"
            onClick={() => navigate('/destinations')}
            className="mb-8"
            data-testid="back-to-destinations"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Destinations
          </Button>

          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Create Your {destination.name} Trip
            </h1>
            <p className="text-xl text-gray-600">
              Choose a starting point for your perfect itinerary
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            {/* Pre-built Itineraries */}
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
              <div className="bg-amber-500 p-6">
                <Calendar className="w-10 h-10 text-white mb-3" />
                <h2 className="text-2xl font-bold text-white">Pre-built Itineraries</h2>
                <p className="text-amber-100 mt-2">
                  Start with our expert-crafted itineraries and customize as you like
                </p>
              </div>
              <div className="p-6 space-y-4">
                {destinationItineraries.length > 0 ? (
                  destinationItineraries.map((itinerary) => (
                    <button
                      key={itinerary.id}
                      onClick={() => handleSelectPrebuilt(itinerary)}
                      data-testid={`itinerary-${itinerary.id}`}
                      className="w-full text-left p-4 rounded-xl border-2 border-gray-100 hover:border-amber-300 hover:bg-amber-50 transition-all"
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className="font-semibold text-gray-900">{itinerary.name}</h3>
                          <p className="text-sm text-gray-600 mt-1">
                            {itinerary.days.length} days of curated experiences
                          </p>
                        </div>
                        <span className="text-amber-600 font-medium text-sm bg-amber-100 px-3 py-1 rounded-full">
                          {itinerary.duration}
                        </span>
                      </div>
                    </button>
                  ))
                ) : (
                  <p className="text-gray-500 text-center py-4">No pre-built itineraries available for this destination yet</p>
                )}
              </div>
            </div>

            {/* Start from Scratch */}
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
              <div className="bg-gray-800 p-6">
                <Plus className="w-10 h-10 text-white mb-3" />
                <h2 className="text-2xl font-bold text-white">Start from Scratch</h2>
                <p className="text-gray-300 mt-2">
                  Build your own unique itinerary from the ground up
                </p>
              </div>
              <div className="p-6">
                <p className="text-gray-600 mb-6">
                  Create a completely custom trip by adding days and selecting activities 
                  from {destination.name}&apos;s {destination.activities.length} available experiences.
                </p>
                <Button
                  onClick={handleStartFromScratch}
                  data-testid="start-from-scratch"
                  className="w-full bg-gray-800 hover:bg-gray-900 text-white py-6"
                >
                  <Plus className="w-5 h-5 mr-2" />
                  Create Custom Itinerary
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Trip Builder Screen
  return (
    <section className="min-h-screen py-20 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Left: Itinerary Builder */}
          <div className="flex-1">
            <Button
              variant="outline"
              onClick={() => setMode(null)}
              className="mb-6"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Options
            </Button>

            <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Your Itinerary</h2>
                  <p className="text-gray-600">
                    {selectedItinerary ? `Based on: ${selectedItinerary.name}` : 'Custom Built'}
                  </p>
                </div>
                <div className="flex items-center gap-2 text-amber-600">
                  <Clock className="w-5 h-5" />
                  <span className="font-semibold">{customDays.length} Days</span>
                </div>
              </div>

              <div className="space-y-4">
                {customDays.map((day, dayIndex) => (
                  <div 
                    key={dayIndex}
                    className="border-2 border-gray-100 rounded-xl p-4 hover:border-amber-200 transition-colors"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-amber-500 rounded-full flex items-center justify-center text-white font-bold">
                          {day.day}
                        </div>
                        <input
                          type="text"
                          value={day.title}
                          onChange={(e) => updateDayTitle(dayIndex, e.target.value)}
                          className="font-semibold text-gray-900 bg-transparent border-b border-transparent hover:border-gray-300 focus:border-amber-500 focus:outline-none"
                        />
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeDay(dayIndex)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        data-testid={`remove-day-${dayIndex}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>

                    {/* Activities for this day */}
                    <div className="ml-12 space-y-2">
                      {day.activities.map((activity, actIndex) => (
                        <div 
                          key={actIndex}
                          className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2"
                        >
                          <span className="text-gray-700">{activity}</span>
                          <div className="flex items-center gap-3">
                            {priceByName[activity] > 0 && (
                              <span className="text-amber-600 font-semibold text-sm">{fmtINR(priceByName[activity])}</span>
                            )}
                            <button
                              onClick={() => removeActivityFromDay(dayIndex, actIndex)}
                              className="text-gray-400 hover:text-red-500 transition-colors"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                      
                      {/* Add activity dropdown */}
                      <select
                        className="w-full p-2 border border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-amber-400 focus:outline-none focus:border-amber-500 cursor-pointer"
                        onChange={(e) => {
                          if (e.target.value) {
                            addActivityToDay(dayIndex, e.target.value);
                            e.target.value = '';
                          }
                        }}
                        defaultValue=""
                      >
                        <option value="">+ Add activity</option>
                        {destination.activities.map((act) => (
                          <option key={act.id} value={act.name}>
                            {act.name}{act.price > 0 ? ` — ${fmtINR(act.price)}` : ''}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                ))}
              </div>

              <Button
                variant="outline"
                onClick={addDay}
                className="w-full mt-4 border-dashed border-2 hover:border-amber-400 hover:bg-amber-50"
                data-testid="add-day"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Another Day
              </Button>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mt-4 flex items-center justify-between" data-testid="itinerary-estimated-total">
              <span className="font-semibold text-gray-800">Estimated activities total</span>
              <span className="text-xl font-bold text-amber-700">{fmtINR(itineraryTotal)}</span>
            </div>
            <p className="text-xs text-gray-500 mt-1 mb-3 text-right">Final quote (incl. hotels, flights & taxes) will be shared by our team.</p>

            <Button
              onClick={handleSubmit}
              size="lg"
              className="w-full bg-amber-600 hover:bg-amber-700 text-white py-6"
              data-testid="submit-itinerary"
            >
              <Send className="w-5 h-5 mr-2" />
              Send This Itinerary Request
            </Button>
          </div>

          {/* Right: Available Activities */}
          <div className="lg:w-96">
            <div className="bg-white rounded-2xl shadow-lg p-6 sticky top-24">
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                Available Activities
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Click to add to your selected day
              </p>
              
              <div className="space-y-2 max-h-[60vh] overflow-y-auto">
                {destination.activities.map((activity) => (
                  <div
                    key={activity.id}
                    className="flex items-center gap-3 p-3 rounded-lg border border-gray-100 hover:border-amber-300 hover:bg-amber-50 transition-all cursor-pointer group"
                  >
                    <img 
                      src={resolveImageUrl(activity.image)} 
                      alt={activity.name}
                      className="w-12 h-12 rounded-lg object-cover"
                    />
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-gray-900 text-sm truncate">
                        {activity.name}
                      </h4>
                      <p className="text-xs text-gray-500 truncate">
                        {activity.description}
                      </p>
                      {activity.price > 0 && (
                        <p className="text-xs font-semibold text-amber-600 mt-0.5">{fmtINR(activity.price)} / person</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TripBuilderPage;
